import { Button, Icon, Typography } from '@equinor/eds-core-react'
import { github, trending_up } from '@equinor/eds-icons'
import { Link } from 'react-router'
import { Alert } from '../../../components/alert'
import AsyncResource from '../../../components/async-resource/async-resource'
import { Breadcrumb } from '../../../components/breadcrumb'
import { CommitHash } from '../../../components/commit-hash'
import { DefaultAppAlias } from '../../../components/component/default-app-alias'
import { DNSAliases } from '../../../components/component/dns-aliases'
import { GitCommitTags } from '../../../components/component/git-commit-tags'
import { DeploymentListContainer } from '../../../components/deployments-list'
import { EventsList } from '../../../components/events-list'
import { GitTagLinks } from '../../../components/git-tags/git-tag-links'
import { ExternalLink } from '../../../components/link/external-link'
import { DeploymentStatusBadge } from '../../../components/status-badges/deployment-status-badge'
import { RelativeToNow } from '../../../components/time/relative-to-now'
import { environmentEventListExpandedKey, useLocalStorage } from '../../../hooks/use-local-storage'
import { routes } from '../../../router/routes'
import { pollingInterval } from '../../../store/defaults'
import {
  radixApi,
  useGetApplicationQuery,
  useGetEnvironmentEventsQuery,
  useGetEnvironmentQuery,
} from '../../../store/radix-api'
import { getFetchErrorMessage } from '../../../store/utils/parse-errors'
import { buildComponentMap } from '../../../utils/build-component-map'
import { getAppDeploymentUrl, getAppUrl, getEnvsUrl } from '../../../utils/routing'
import { dataSorter, sortCompareDate } from '../../../utils/sort-utils'
import { linkToGitHubBranch, routeWithParams, smallDeploymentName } from '../../../utils/string'
import { ActiveJobsList } from './ActiveJobsList'
import { ActiveComponentsList } from './active-components/ActiveComponentsList'
import EnvironmentAlerting from './EnvironmentAlerting'
import EnvironmentToolbar from './EnvironmentToolbar'

import '../style.css'

type Props = {
  appName: string
  envName: string
}
export const EnvironmentOverview = ({ appName, envName }: Props) => {
  const { data: application } = useGetApplicationQuery({ appName }, { skip: !appName, pollingInterval })
  const {
    data: environment,
    refetch: refetchEnv,
    ...envState
  } = useGetEnvironmentQuery({ appName, envName }, { skip: !appName || !envName, pollingInterval })
  const [isEventListExpanded, setIsEventListExpanded] = useLocalStorage(environmentEventListExpandedKey, true)
  const { data: events } = useGetEnvironmentEventsQuery(
    { appName, envName },
    { skip: !appName || !envName || !isEventListExpanded, pollingInterval }
  )
  const [deleteEnvTrigger, deleteEnvState] = radixApi.endpoints.deleteEnvironment.useMutation()
  const { appAlias, dnsAliases, dnsExternalAliases } = application ?? {}
  const envDNSAliases = dnsAliases ? dnsAliases.filter((alias) => alias.environmentName == envName) : []
  const envDNSExternalAliases = dnsExternalAliases
    ? dnsExternalAliases.filter((alias) => alias.environmentName == envName)
    : []

  const isLoaded = application && environment
  const isOrphan = environment?.status === 'Orphan'
  const deployment = isLoaded && environment.activeDeployment
  const skippedDeployComponents = deployment?.components?.filter((c) => c.skipDeployment) ?? false

  const { components: activeComponents, jobs: activeJobs } = buildComponentMap(deployment?.components ?? [])

  return (
    <>
      <Breadcrumb
        links={[
          { label: appName, to: getAppUrl(appName) },
          { label: 'Environments', to: getEnvsUrl(appName) },
          { label: envName },
        ]}
      />

      {(deleteEnvState.isSuccess || deleteEnvState.isError || isOrphan) && (
        <div className="grid grid--gap-medium">
          {deleteEnvState.isSuccess && (
            <Alert>Environment removal has started but it may take a while to be completely removed</Alert>
          )}
          {deleteEnvState.isError && (
            <Alert type="warning">Some unexpected error occurred: {getFetchErrorMessage(deleteEnvState.error)}</Alert>
          )}
          {isOrphan && (
            <Alert
              type="warning"
              actions={
                <Button
                  disabled={deleteEnvState.isLoading || deleteEnvState.isSuccess}
                  onClick={async () => {
                    if (window.confirm(`Confirm deleting '${envName}' environment.`)) {
                      await deleteEnvTrigger({ appName, envName })
                    }
                  }}
                >
                  Delete environment
                </Button>
              }
            >
              <Typography>
                This environment is orphaned; it is not defined in <strong>radixconfig.yaml</strong>
              </Typography>
            </Alert>
          )}
        </div>
      )}
      <AsyncResource asyncState={envState}>
        {isLoaded && (
          <>
            <section className="grid grid--gap-medium">
              <EnvironmentToolbar
                appName={appName}
                environment={environment}
                startEnabled
                stopEnabled
                refetch={refetchEnv}
              />
              <Typography variant="h4">Overview</Typography>
              <div className="grid grid--gap-medium grid--overview-columns">
                <div className="grid grid--gap-medium">
                  <Typography>
                    Environment <strong>{envName}</strong>
                  </Typography>
                  {environment.activeDeployment ? (
                    <Typography>
                      Built and deployed from {environment.activeDeployment?.gitRefType ?? 'branch'}{' '}
                      <ExternalLink
                        href={linkToGitHubBranch(
                          application.registration?.repository ?? '',
                          environment.activeDeployment?.gitRef ?? environment.branchMapping ?? ''
                        )}
                      >
                        {environment.activeDeployment.gitRef ?? environment.branchMapping ?? ''}
                      </ExternalLink>{' '}
                      {deployment?.gitCommitHash && (
                        <>
                          {' commit '}
                          <CommitHash
                            repo={application.registration?.repository ?? ''}
                            commit={deployment.gitCommitHash}
                          />
                        </>
                      )}
                    </Typography>
                  ) : (
                    <Typography>Not automatically deployed</Typography>
                  )}
                  {skippedDeployComponents && (
                    <>
                      {skippedDeployComponents.map((component) => (
                        <Typography key={component.name}>
                          <strong>{component.name}</strong>
                          <>
                            {' keeps deployment '}
                            <GitCommitTags
                              commitID={component.commitID}
                              // gitTags={component.gitTags}
                              repository={deployment?.repository ?? ''}
                            />
                          </>
                        </Typography>
                      ))}
                    </>
                  )}
                  <div>
                    <EnvironmentAlerting appName={appName} envName={envName} />
                  </div>
                </div>
                <div className="grid grid--gap-medium">
                  {deployment ? (
                    <>
                      <Typography>
                        Deployment active since{' '}
                        <strong>
                          {/* TODO #1422 - deployment.activeFrom could be undefined, resulting in empty display */}
                          <RelativeToNow time={deployment.activeFrom} />
                        </strong>
                      </Typography>
                      <Typography>
                        Active deployment{' '}
                        <Typography as={Link} to={getAppDeploymentUrl(appName, deployment.name)} link>
                          {smallDeploymentName(deployment.name)}
                        </Typography>{' '}
                        <Button
                          variant="ghost"
                          as={Link}
                          to={routeWithParams(
                            routes.appJobNew,
                            { appName: appName },
                            {
                              pipeline: 'promote',
                              deploymentName: deployment.name,
                              fromEnvironment: deployment.environment,
                            }
                          )}
                        >
                          Promote <Icon data={trending_up} />
                        </Button>
                      </Typography>
                      <div className="grid grid--gap-small grid--auto-columns">
                        <Typography>Status</Typography>
                        <DeploymentStatusBadge status={deployment.status} />
                      </div>
                      {deployment.gitTags && (
                        <div className="environment-overview__tags grid grid--gap-x-small grid--auto-columns">
                          <Typography>Tags</Typography>
                          <GitTagLinks
                            gitTags={deployment.gitTags}
                            repository={application.registration?.repository ?? ''}
                          />
                          <Icon data={github} size={24} />
                        </div>
                      )}
                    </>
                  ) : (
                    <Typography>No active deployment</Typography>
                  )}
                </div>
              </div>
            </section>
            {deployment && deployment.status === 'Failed' && !!deployment.statusReason && (
              <Alert type="danger">
                <Typography variant="h4">Deployment Error</Typography>
                <Typography variant="body_long">{deployment?.statusReason}</Typography>
              </Alert>
            )}
            {appAlias?.environmentName == envName && <DefaultAppAlias appName={appName} appAlias={appAlias} />}
            {envDNSAliases?.length > 0 && (
              <DNSAliases appName={appName} dnsAliases={envDNSAliases} title={'DNS aliases'} />
            )}
            {envDNSExternalAliases?.length > 0 && (
              <DNSAliases appName={appName} dnsAliases={envDNSExternalAliases} title={'DNS external aliases'} />
            )}

            {activeComponents.length > 0 && (
              <ActiveComponentsList appName={appName} envName={envName} components={activeComponents} />
            )}
            {activeJobs.length > 0 && <ActiveJobsList appName={appName} envName={envName} jobs={activeJobs} />}

            <EventsList
              isExpanded={isEventListExpanded}
              onExpanded={setIsEventListExpanded}
              events={dataSorter(events ?? [], [
                ({ lastTimestamp: x }, { lastTimestamp: y }) => sortCompareDate(x, y, 'descending'),
              ])}
            />
            {environment.deployments && (
              <div className="grid grid--gap-medium">
                <Typography variant="h4">Previous deployments</Typography>
                <DeploymentListContainer
                  inEnv
                  appName={appName}
                  deployments={environment.deployments.filter(({ activeTo }) => !!activeTo)}
                />
              </div>
            )}
          </>
        )}
      </AsyncResource>
    </>
  )
}

export default EnvironmentOverview
